import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { OrgEmailNotifications, OrgGeneralNotifications } from 'models/organization';

type PlacedOrderSetting = 'orderSentByLab' | 'orderDeclined' | 'placedOrderNewMessage' | 'orderReceivedByLab';

// DD-3492 (PO answers A1/A2): what a lab hears about the orders it placed with a
// partner lab, kept apart from the orders it receives. "Accepted" was not requested
// and is off unless the lab turns it on. The defaults mirror shared-denthub-code's
// shouldNotifyOrganization / shouldEmailOrganization for the customer side.
const PLACED_ORDER_SETTINGS: { name: PlacedOrderSetting; label: string; inApplicationDefault: boolean }[] = [
    { name: 'orderSentByLab', label: 'PLACED_ORDER_SENT', inApplicationDefault: true },
    { name: 'orderDeclined', label: 'PLACED_ORDER_DECLINED', inApplicationDefault: true },
    { name: 'placedOrderNewMessage', label: 'PLACED_ORDER_NEW_MESSAGE', inApplicationDefault: true },
    { name: 'orderReceivedByLab', label: 'PLACED_ORDER_ACCEPTED', inApplicationDefault: false },
];

type PlacedOrderNotificationSettingTableProps = {
    generalNotifications: OrgGeneralNotifications;
    emailNotifications: OrgEmailNotifications;
    handleGeneralNotifications: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleEmailNotifications: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const PlacedOrderNotificationSettingTable: FC<PlacedOrderNotificationSettingTableProps> = ({
    generalNotifications,
    emailNotifications,
    handleGeneralNotifications,
    handleEmailNotifications,
}: PlacedOrderNotificationSettingTableProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);

    return (
        <Box mb={4} data-test="placed-order-notification-settings">
            <Typography variant="h3">{translate('lab-settings:PLACED_ORDER_NOTIFICATIONS')}</Typography>
            <Typography variant="body2" color="text.secondary">
                {translate('lab-settings:PLACED_ORDER_NOTIFICATIONS_INFO')}
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="60%">{translate('lab-settings:NOTIFICATION')}</TableCell>
                            <TableCell>{translate('lab-settings:IN_APPLICATION')}</TableCell>
                            <TableCell>{translate('common:EMAIL')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {PLACED_ORDER_SETTINGS.map(({ name, label, inApplicationDefault }) => (
                            <TableRow key={name}>
                                <TableCell>{translate(`lab-settings:${label}`)}</TableCell>
                                <TableCell>
                                    <Switch
                                        checked={generalNotifications[name] ?? inApplicationDefault}
                                        onChange={handleGeneralNotifications}
                                        name={name}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        checked={emailNotifications[name] ?? false}
                                        onChange={handleEmailNotifications}
                                        name={name}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default PlacedOrderNotificationSettingTable;
